/**
 * Cart drawer add-on: the optional shipping protection line.
 *
 * Ticking the box adds the product chosen in Theme settings → Cart drawer →
 * "Shipping protection product"; unticking removes that line again.
 *
 * Two things the cart API is picky about, both learned the hard way:
 *   • `/cart/change.js` wants `id` as a STRING (a plain integer, or `variant:key`).
 *     A JSON number is rejected with HTTP 400 "expected Integer …: id".
 *   • the line key from the add response is the safest id to remove with, so it is
 *     remembered on the checkbox and used when available.
 *
 * The change listener is delegated from `document` so it survives the drawer morphing
 * after each update. The refresh follows the theme's contract: the mutation is wrapped
 * in a promise resolving to `{ detail: <cart response> }` and dispatched as a standard
 * CartLinesUpdateEvent, which is what CartItemsComponent morphs the drawer from.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const SECTION = 'cart-drawer-section';

async function toggle(input, adding) {
  const variantId = Number(input.dataset.variantId);
  if (!variantId) return;

  input.disabled = true;

  // The change endpoint takes a string id: the line key when we know it.
  const lineId = adding ? String(variantId) : input.dataset.lineKey || String(variantId);
  const body = adding
    ? { items: [{ id: variantId, quantity: 1 }] }
    : { id: lineId, quantity: 0 };
  // Ask Shopify to render the drawer alongside the mutation, so the theme has fresh
  // markup to morph in and no second request is needed.
  body.sections = [SECTION];
  body.sections_url = window.location.pathname;

  const promise = fetch(adding ? '/cart/add.js' : '/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(body),
  })
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    })
    .then((detail) => {
      // Remember the line key of what we just added, for the next removal.
      const key = detail?.items?.find((item) => Number(item.variant_id) === variantId)?.key;
      if (key) input.dataset.lineKey = key;
      return { detail };
    });

  promise.catch((error) => {
    console.warn('[cart add-ons] protection toggle failed:', error);
    input.checked = !adding;
    input.disabled = false;
  });

  document.dispatchEvent(new CartLinesUpdateEvent({ promise }));
}

document.addEventListener('change', (event) => {
  const input = event.target?.closest?.('[data-cart-protection]');
  if (!input) return;
  toggle(input, input.checked);
});
